/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedCurrency from 'I18n/components/FormattedCurrency';
import H2 from 'UIComponents/elements/headings/H2';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UICurrencyInput from 'UIComponents/input/UICurrencyInput';
import UISelect from 'UIComponents/input/UISelect';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { getUserRequestedRefundReasons } from '../../../constants/refundReasons';
import { useRefundProperties } from '../../../hooks/useRefundProperties';
import { useRefundTransactionMutation } from '../../../hooks/useRefundTransactionMutation';
import { usageTracker } from '../../../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function showSuccessAlert() {
  FloatingAlertStore.addAlert({
    message: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refundSuccessAlert"
    }),
    type: 'success'
  });
}
function showErrorAlert() {
  FloatingAlertStore.addAlert({
    message: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refundError.genericAlert"
    }),
    type: 'danger'
  });
}
function RefundModal({
  objectId,
  paymentSource,
  onRefundSuccess,
  onClose
}) {
  const [refundAmount, setRefundAmount] = useState(undefined);
  const [refundReason, setRefundReason] = useState('');
  const {
    invoiceId,
    isRefundable,
    currencyCode,
    maxRefundableAmount
  } = useRefundProperties(objectId);
  const handleRefundModalClose = useCallback(() => {
    setRefundAmount(0);
    setRefundReason('');
    onClose();
  }, [setRefundAmount, setRefundReason, onClose]);
  const handleRefundModalCancel = useCallback(() => {
    usageTracker.track('paymentCRMSidebarInteraction', {
      action: 'refundModalCancelClicked'
    });
    handleRefundModalClose();
  }, [handleRefundModalClose]);
  const [refundTransaction, {
    loading
  }] = useRefundTransactionMutation({
    invoiceId,
    paymentObjectId: +objectId,
    refundAmount: refundAmount,
    // Guaranteed to have a value because submit btn is disabled unless refundAmount truthy
    refundReason,
    onError: showErrorAlert,
    onSuccess() {
      usageTracker.track('paymentCRMSidebarUsage', {
        action: 'refundModalSubmitClicked'
      });
      showSuccessAlert();
      onRefundSuccess();
      handleRefundModalClose();
    }
  });
  const handleRefund = useCallback(() => {
    refundTransaction();
  }, [refundTransaction]);
  return /*#__PURE__*/_jsx(UIModal, {
    "data-test-id": "refund-modal",
    children: /*#__PURE__*/_jsxs(UIForm, {
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.header"
          })
        }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: handleRefundModalClose
        })]
      }), /*#__PURE__*/_jsxs(UIDialogBody, {
        children: [paymentSource !== 'INTEGRATION' && /*#__PURE__*/_jsx("p", {
          className: "m-bottom-6",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.timeline.default"
          })
        }), /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsxs("strong", {
            "data-test-id": "max-refundable-amount",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.maxRefundableAmount"
            }), maxRefundableAmount !== undefined && /*#__PURE__*/_jsx(FormattedCurrency, {
              value: maxRefundableAmount,
              currencyCode: currencyCode
            })]
          })
        }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(UIFormControl, {
          required: true,
          label: I18n.text('cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refundAmount'),
          children: /*#__PURE__*/_jsx(UICurrencyInput, {
            "data-test-id": "refund-amount-input",
            autoFocus: true,
            min: 0,
            max: maxRefundableAmount,
            value: refundAmount,
            currency: currencyCode,
            onChange: evt => {
              if (evt.target.value === null || evt.target.value === undefined) {
                return setRefundAmount(undefined);
              }
              return setRefundAmount(parseFloat(evt.target.value));
            },
            placeholder: I18n.formatCurrency(maxRefundableAmount || 0, {
              currencyCode
            })
          })
        }), /*#__PURE__*/_jsx(UIFormControl, {
          required: true,
          label: I18n.text('cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refundReason'),
          children: /*#__PURE__*/_jsx(UISelect, {
            "data-test-id": "refund-reason-input",
            value: refundReason,
            onChange: evt => setRefundReason(evt.target.value),
            options: getUserRequestedRefundReasons(),
            placeholder: I18n.text('cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refundReasonPlaceholder')
          })
        })]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(UILoadingButton, {
          "data-test-id": "submit-refund-request",
          use: "primary",
          onClick: handleRefund,
          loading: loading,
          disabled: !refundAmount || !refundReason || !isRefundable,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.refund"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: handleRefundModalCancel,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cardPaymentHighlight.paymentHighlight.sidebarActions.refundPayment.modal.cancel"
          })
        })]
      })]
    })
  });
}
RefundModal.propTypes = {
  objectId: PropTypes.string.isRequired,
  onRefundSuccess: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};
export default RefundModal;